import React, { useState, useEffect, useRef } from "react";

interface DataSimulationProps {
  onMetricsUpdate?: (metrics: {
    automatedTasks: number;
    timeOptimized: number;
    costsReduced: number;
  }) => void;
}

const DataSimulation: React.FC<DataSimulationProps> = ({ onMetricsUpdate }) => {
  const [selectedSimulation, setSelectedSimulation] =
    useState<string>("invoice");
  const [isRunning, setIsRunning] = useState<boolean>(true);
  const [dataPoints, setDataPoints] = useState<
    Array<{ manual: number; automated: number }>
  >([]);
  const [stats, setStats] = useState<{
    manualAvg: number;
    automatedAvg: number;
    improvement: number;
  }>({
    manualAvg: 0,
    automatedAvg: 0,
    improvement: 0,
  });
  const [animatedImprovement, setAnimatedImprovement] = useState(0);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  const simulations = [
    {
      id: "invoice",
      name: "Invoice Processing",
      description: "Manual vs AI-Automated Invoice Processing Time (minutes)",
      manualAvg: 20,
      manualVariance: 10,
      automatedAvg: 2,
      automatedVariance: 1,
      icon: "📄",
    },
    {
      id: "support",
      name: "Customer Support",
      description:
        "Manual vs AI-Automated Customer Query Resolution Time (minutes)",
      manualAvg: 15,
      manualVariance: 8,
      automatedAvg: 2.5,
      automatedVariance: 1.2,
      icon: "💬",
    },
    {
      id: "quality",
      name: "Quality Control",
      description: "Manual vs AI-Automated Error Rate (%)",
      manualAvg: 12,
      manualVariance: 5,
      automatedAvg: 3,
      automatedVariance: 1.5,
      icon: "🔍",
    },
  ];

  const generateDataPoint = () => {
    const simulation = simulations.find((sim) => sim.id === selectedSimulation);
    if (!simulation) return;

    const { manualAvg, manualVariance, automatedAvg, automatedVariance } =
      simulation;

    // Generate slightly random values based on average and variance
    const manual = Math.max(
      0.5,
      manualAvg + (Math.random() - 0.5) * manualVariance
    );
    const automated = Math.max(
      0.1,
      automatedAvg + (Math.random() - 0.5) * automatedVariance
    );

    return { manual, automated };
  };

  // Start/stop the simulation
  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      const newPoint = generateDataPoint();
      if (!newPoint) return;

      setDataPoints((prev) => {
        const newData = [...prev, newPoint];
        // Keep last 20 points only
        return newData.slice(Math.max(0, newData.length - 20));
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, selectedSimulation]);

  // Animate the improvement percentage
  useEffect(() => {
    // Smoothly animate from current value to target
    if (stats.improvement === 0) return;

    let startValue = animatedImprovement;
    const endValue = stats.improvement;
    const duration = 1000; // 1 second animation
    const startTime = performance.now();

    const animateValue = (timestamp: number) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smoother animation
      const easedProgress =
        progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);

      const currentValue = startValue + (endValue - startValue) * easedProgress;
      setAnimatedImprovement(currentValue);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animateValue);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animateValue);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [stats.improvement]);

  // Calculate stats whenever dataPoints change
  useEffect(() => {
    if (dataPoints.length === 0) return;

    const manualSum = dataPoints.reduce((sum, point) => sum + point.manual, 0);
    const automatedSum = dataPoints.reduce(
      (sum, point) => sum + point.automated,
      0
    );

    const manualAvg = parseFloat((manualSum / dataPoints.length).toFixed(2));
    const automatedAvg = parseFloat(
      (automatedSum / dataPoints.length).toFixed(2)
    );
    const improvement = parseFloat(
      (((manualAvg - automatedAvg) / manualAvg) * 100).toFixed(2)
    );

    const newStats = { manualAvg, automatedAvg, improvement };

    // Only update if the stats have actually changed
    if (
      stats.manualAvg !== newStats.manualAvg ||
      stats.automatedAvg !== newStats.automatedAvg ||
      stats.improvement !== newStats.improvement
    ) {
      setStats(newStats);

      // Update parent component metrics if callback provided
      if (onMetricsUpdate) {
        onMetricsUpdate({
          automatedTasks: Math.round(dataPoints.length * 1.5),
          timeOptimized: Math.round(improvement),
          costsReduced: Math.round(improvement * 0.8),
        });
      }
    }
  }, [dataPoints]);

  // Draw chart
  useEffect(() => {
    if (!canvasRef.current || dataPoints.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Get device pixel ratio for better rendering on high DPI screens
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    // Set canvas dimensions accounting for device pixel ratio
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Set display size (CSS)
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Find max value for scaling
    const allValues = dataPoints.flatMap((p) => [p.manual, p.automated]);
    const maxValue = Math.max(...allValues) * 1.1; // Add 10% padding

    // Draw grid lines
    ctx.strokeStyle = "#1e293b";
    ctx.lineWidth = 1;

    // Horizontal grid lines
    for (let i = 0; i <= 5; i++) {
      const y = rect.height - (i / 5) * rect.height;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(rect.width, y);
      ctx.stroke();
    }

    // Y-axis labels
    ctx.fillStyle = "#94a3b8";
    ctx.font = "10px sans-serif";
    for (let i = 0; i <= 5; i++) {
      const value = ((maxValue * i) / 5).toFixed(1);
      const y = rect.height - (i / 5) * rect.height;
      ctx.fillText(value, 5, y - 5);
    }

    // Function to draw data line with gradient
    const drawDataLine = (data: number[], color: string, isManual: boolean) => {
      // Line gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, rect.height);
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, "rgba(0,0,0,0)");

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();

      // Area fill
      if (!isManual) {
        ctx.fillStyle = color.replace(")", ", 0.1)").replace("rgb", "rgba");
      }

      data.forEach((value, index) => {
        const x = (index / (data.length - 1)) * rect.width;
        const y = rect.height - (value / maxValue) * rect.height;

        if (index === 0) {
          ctx.moveTo(x, y);
          if (!isManual) ctx.lineTo(x, rect.height);
        } else {
          ctx.lineTo(x, y);
        }
      });

      if (!isManual) {
        const lastX = ((data.length - 1) / (data.length - 1)) * rect.width;
        ctx.lineTo(lastX, rect.height);
        ctx.fill();
      }

      ctx.stroke();

      // Draw data points
      data.forEach((value, index) => {
        const x = (index / (data.length - 1)) * rect.width;
        const y = rect.height - (value / maxValue) * rect.height;

        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    // Draw manual data line
    drawDataLine(
      dataPoints.map((p) => p.manual),
      "#ef4444",
      true
    );

    // Draw automated data line with area fill
    drawDataLine(
      dataPoints.map((p) => p.automated),
      "#10b981",
      false
    );
  }, [dataPoints]);

  // Handle simulation selection
  const handleSimulationChange = (simulationId: string) => {
    setSelectedSimulation(simulationId);
    setDataPoints([]);
  };

  // Handle start/stop
  const toggleSimulation = () => {
    setIsRunning((prev) => !prev);
  };

  const currentSimulation = simulations.find(
    (sim) => sim.id === selectedSimulation
  );

  return (
    <div className="bg-[#0A0A1B] p-4 md:p-6 rounded-xl max-w-4xl mx-auto">
      <h2 className="text-xl md:text-2xl font-bold text-center text-purple-400 mb-4 md:mb-6">
        Real-time Data Simulation
      </h2>
      <p className="text-sm text-gray-400 text-center mb-6 max-w-2xl mx-auto">
        See how AI automation compares to manual processes in real-time. Watch
        as the data updates dynamically to show performance improvements.
      </p>

      {/* Simulation Type Selection */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {simulations.map((sim) => (
          <div
            key={sim.id}
            className={`p-3 md:p-4 rounded-lg cursor-pointer transition-all duration-300 text-center
              ${
                selectedSimulation === sim.id
                  ? "bg-gray-800 border border-purple-500 shadow-md transform scale-105"
                  : "bg-gray-800/50 hover:bg-gray-800/80 hover:transform hover:scale-102"
              }`}
            onClick={() => handleSimulationChange(sim.id)}
          >
            <div className="text-2xl mb-2">{sim.icon}</div>
            <h3 className="font-medium mb-1 text-sm md:text-base">
              {sim.name}
            </h3>
            <p className="text-xs text-gray-400 hidden md:block">
              {sim.description}
            </p>
          </div>
        ))}
      </div>

      {/* Stats Display */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {/* Manual Average */}
        <div className="bg-gray-800/50 p-3 rounded-lg relative overflow-hidden">
          <p className="text-xs text-gray-400 mb-1">Manual Average</p>
          <p className="text-lg md:text-xl font-bold text-red-400">
            {stats.manualAvg.toFixed(1)}
          </p>
          <div
            className="absolute bottom-0 left-0 h-1 bg-red-400"
            style={{ width: `${(stats.manualAvg / 30) * 100}%` }}
          ></div>
        </div>

        {/* AI Average */}
        <div className="bg-gray-800/50 p-3 rounded-lg relative overflow-hidden">
          <p className="text-xs text-gray-400 mb-1">AI Average</p>
          <p className="text-lg md:text-xl font-bold text-green-400">
            {stats.automatedAvg.toFixed(1)}
          </p>
          <div
            className="absolute bottom-0 left-0 h-1 bg-green-400"
            style={{ width: `${(stats.automatedAvg / 30) * 100}%` }}
          ></div>
        </div>

        {/* Improvement */}
        <div className="bg-gray-800/50 p-3 rounded-lg relative overflow-hidden">
          <p className="text-xs text-gray-400 mb-1">Improvement</p>
          <div className="flex items-baseline">
            <p className="text-lg md:text-xl font-bold text-blue-400">
              +{Math.round(animatedImprovement)}
            </p>
            <p className="text-sm text-blue-400 ml-0.5">%</p>
          </div>
          <div
            className="absolute bottom-0 left-0 h-1 bg-blue-400 transition-all duration-500"
            style={{ width: `${(animatedImprovement / 100) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Chart */}
      <div
        className="relative bg-gray-900/30 rounded-lg p-2 mb-6 overflow-hidden"
        style={{ height: "240px" }}
      >
        <canvas ref={canvasRef} className="w-full h-full"></canvas>

        {/* Legend */}
        <div className="absolute top-2 right-2 flex flex-col md:flex-row items-start md:items-center md:space-x-4 space-y-1 md:space-y-0 bg-gray-800/80 px-2 py-1 rounded text-xs">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-400 rounded-full mr-1"></div>
            <span className="text-gray-300">Manual Process</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full mr-1"></div>
            <span className="text-gray-300">AI Automated</span>
          </div>
        </div>

        {/* Current simulation description */}
        <div className="absolute bottom-2 left-2 right-2 bg-gray-800/80 px-2 py-1 rounded text-xs text-gray-300 overflow-hidden text-ellipsis whitespace-nowrap">
          {currentSimulation?.description}
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-center">
        <button
          onClick={toggleSimulation}
          className={`px-4 py-2 rounded-lg font-medium flex items-center
            ${
              isRunning
                ? "bg-red-500 hover:bg-red-600"
                : "bg-green-500 hover:bg-green-600"
            } transition-colors duration-200 shadow-md transform hover:translate-y-px`}
        >
          <span className="mr-2">
            {isRunning ? "Pause" : "Start"} Simulation
          </span>
          {isRunning ? (
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ) : (
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default DataSimulation;
